/**
 * Vendor agreements, one per module.
 *
 * Each upload is a new version — nothing is edited or deleted, because a vendor's
 * acceptance has to keep pointing at the exact document they agreed to. Publishing
 * notifies only the vendors offering that module, and the acceptance count is the
 * thing this screen exists to answer: who has not signed the current terms yet.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CheckCircle2,
  Download,
  FileSignature,
  FileText,
  History,
  Loader2,
  Upload,
} from 'lucide-react';
import { DashboardLayout } from '@/components/dashboard-layout';
import { DetailPanel, PanelSkeleton } from '@/components/detail/detail-panel';
import { open_file_handler } from '@/components/auth-image';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { ErrorState } from '@/components/ui/error-state';
import { Label } from '@/components/ui/label';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { notify, error_message } from '@/components/ui/toaster';
import { cn } from '@/lib/utils';
import { date_short } from '@/lib/format';
import { VENDOR_MODULE_TYPE_LABELS, type VendorModuleType } from '@/features/vendors/types';
import {
  use_agreement_overview,
  use_agreement_vendors,
  use_agreement_versions,
  use_publish_agreement,
} from '@/features/agreements/hooks';
import {
  ACCEPTANCE_SOURCE_LABEL,
  MAX_AGREEMENT_BYTES,
  MAX_CHANGE_SUMMARY,
  file_size,
  type AgreementModuleOverview,
  type AgreementVersion,
} from '@/features/agreements/types';

const module_label = (m: VendorModuleType) => VENDOR_MODULE_TYPE_LABELS[m] ?? m;
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** Today as an `<input type="date">` value, in the admin's local time. */
function today_input(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function AgreementsPage() {
  const { data, isLoading, error, refetch } = use_agreement_overview();
  const [uploading, set_uploading] = useState<AgreementModuleOverview | null>(null);
  const [history_for, set_history_for] = useState<AgreementModuleOverview | null>(null);

  const summary = useMemo(() => {
    if (!data) return undefined;
    const published = data.filter((m) => m.latest).length;
    const pending = data.reduce((n, m) => n + Math.max(0, m.vendor_count - m.accepted_count), 0);
    return `${published} of ${data.length} modules have an agreement · ${plural(pending, 'vendor')} yet to accept the latest`;
  }, [data]);

  return (
    <DashboardLayout
      title="Agreements"
      subtitle="Per-module vendor agreements — publish new versions and track who has accepted"
    >
      <DetailPanel
        title="Agreements by module"
        icon={FileSignature}
        description={summary}
        body_class="p-0"
        test_id="agreement-list"
      >
        {error ? (
          <div className="p-3.5">
            <ErrorState
              title="Could not load the agreements"
              description={error_message(error, 'Nothing has been changed. Try again.')}
              action={
                <Button size="sm" variant="outline" onClick={() => void refetch()}>
                  Retry
                </Button>
              }
            />
          </div>
        ) : isLoading || !data ? (
          <div className="p-3.5">
            <PanelSkeleton rows={6} />
          </div>
        ) : data.length === 0 ? (
          <div className="p-3.5">
            <EmptyState icon={FileText} title="No modules take agreements" />
          </div>
        ) : (
          <ul className="divide-y divide-border" data-testid="agreement-rows">
            {data.map((m) => (
              <ModuleRow
                key={m.module_type}
                row={m}
                on_upload={() => set_uploading(m)}
                on_history={() => set_history_for(m)}
              />
            ))}
          </ul>
        )}
      </DetailPanel>

      <UploadDialog row={uploading} on_close={() => set_uploading(null)} />
      <HistorySheet row={history_for} on_close={() => set_history_for(null)} />
    </DashboardLayout>
  );
}

function ModuleRow({
  row,
  on_upload,
  on_history,
}: {
  row: AgreementModuleOverview;
  on_upload: () => void;
  on_history: () => void;
}) {
  const { latest } = row;
  const all_accepted = row.vendor_count > 0 && row.accepted_count >= row.vendor_count;
  return (
    <li
      className="grid items-center gap-x-4 gap-y-2 px-3.5 py-3 lg:grid-cols-[12rem_1fr_9rem_auto]"
      data-testid={`agreement-row-${row.module_type}`}
    >
      <span className="font-heading text-data font-bold">{module_label(row.module_type)}</span>

      <div className="flex min-w-0 flex-col gap-0.5">
        {latest ? (
          <>
            <span className="flex flex-wrap items-center gap-1.5">
              <Badge tone="primary" size="sm">
                v{latest.version}
              </Badge>
              <span className="text-sm">Published {date_short(latest.published_at)}</span>
              {!latest.in_effect && (
                <Badge tone="info" size="sm" dot data-testid={`agreement-upcoming-${row.module_type}`}>
                  Upcoming
                </Badge>
              )}
            </span>
            <span className="truncate text-micro text-muted-foreground" title={latest.original_filename}>
              {latest.in_effect ? 'In effect since' : 'Takes effect'} {date_short(latest.effective_from)} ·{' '}
              {latest.original_filename}
            </span>
          </>
        ) : (
          <span className="text-sm text-muted-foreground">No agreement published yet</span>
        )}
      </div>

      <div className="flex flex-col gap-0.5" data-testid={`agreement-acceptance-${row.module_type}`}>
        {latest ? (
          <>
            <span
              className={cn(
                'tabular font-heading text-data font-bold',
                all_accepted ? 'text-success' : undefined,
              )}
            >
              {row.accepted_count} / {row.vendor_count}
            </span>
            <span className="text-micro text-muted-foreground">accepted latest</span>
          </>
        ) : (
          <span className="text-micro text-muted-foreground">
            {plural(row.vendor_count, 'vendor')} offering this
          </span>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" onClick={on_upload} data-testid={`agreement-upload-${row.module_type}`}>
          <Upload className="mr-1 h-4 w-4" /> Upload new version
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={on_history}
          disabled={!latest}
          data-testid={`agreement-history-${row.module_type}`}
        >
          <History className="mr-1 h-4 w-4" /> History
        </Button>
        <Button
          size="sm"
          variant="ghost"
          disabled={!latest}
          onClick={
            latest ? open_file_handler(latest.file_path, (msg) => notify.error(msg)) : undefined
          }
          data-testid={`agreement-download-${row.module_type}`}
        >
          <Download className="mr-1 h-4 w-4" /> Download
        </Button>
      </div>
    </li>
  );
}

/**
 * Publishing a version. There is no edit and no undo — every vendor offering the module
 * is notified the moment it is saved — so the dialog says exactly who will hear about it.
 */
function UploadDialog({
  row,
  on_close,
}: {
  row: AgreementModuleOverview | null;
  on_close: () => void;
}) {
  const open = row !== null;
  const file_input = useRef<HTMLInputElement>(null);
  const [file, set_file] = useState<File | null>(null);
  const [effective, set_effective] = useState(today_input());
  const [summary, set_summary] = useState('');
  const [error, set_error] = useState<string | null>(null);
  const publish_m = use_publish_agreement();
  const busy = publish_m.isPending;

  // A fresh form per opening — a half-written summary must never ride along to another module.
  useEffect(() => {
    if (!open) return;
    set_file(null);
    set_effective(today_input());
    set_summary('');
    set_error(null);
  }, [open, row?.module_type]);

  useEffect(() => {
    if (!open) return;
    const on_key = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) on_close();
    };
    window.addEventListener('keydown', on_key);
    return () => window.removeEventListener('keydown', on_key);
  }, [open, busy, on_close]);

  if (!row) return null;
  const label = module_label(row.module_type);
  const today = today_input();
  const is_future = effective > today;

  function pick(f: File | null) {
    set_error(null);
    if (!f) return set_file(null);
    if (f.type !== 'application/pdf' && !f.name.toLowerCase().endsWith('.pdf')) {
      set_file(null);
      if (file_input.current) file_input.current.value = '';
      return set_error('The agreement must be a PDF.');
    }
    if (f.size > MAX_AGREEMENT_BYTES) {
      set_file(null);
      if (file_input.current) file_input.current.value = '';
      return set_error(`That file is ${file_size(f.size)} — the limit is 10 MB.`);
    }
    set_file(f);
  }

  async function submit() {
    if (!row) return;
    set_error(null);
    if (!file) return set_error('Choose the PDF to publish.');
    if (!effective) return set_error('Pick the date this version takes effect.');
    try {
      const created = await publish_m.mutateAsync({
        module_type: row.module_type,
        file,
        // Today means "now" — let the server stamp it. A later date is local midnight.
        effective_from: is_future ? new Date(`${effective}T00:00:00`).toISOString() : undefined,
        change_summary: summary.trim() || undefined,
      });
      notify.success(
        `Published ${label} v${created.version} — notified ${plural(created.notified_vendors, 'vendor')}`,
        {
          description: created.in_effect
            ? 'In effect now.'
            : `Takes effect ${date_short(created.effective_from)}.`,
        },
      );
      on_close();
    } catch (e) {
      set_error(error_message(e, 'Could not publish the agreement.'));
    }
  }

  const next_version = (row.latest?.version ?? 0) + 1;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Upload ${label} agreement`}
      data-testid="agreement-upload-dialog"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    >
      <div className="max-h-full w-full max-w-lg overflow-y-auto rounded-lg border border-border bg-card p-6 shadow-l2">
        <h2 className="font-heading text-xl font-bold leading-tight">
          Upload {label} agreement v{next_version}
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Published versions are permanent — they cannot be edited or removed.
          {row.latest ? ` Replaces v${row.latest.version} once it takes effect.` : ''}
        </p>

        <div className="mt-5 grid gap-4">
          <div className="grid gap-1.5">
            <Label htmlFor="ag-file">Agreement PDF</Label>
            <input
              id="ag-file"
              ref={file_input}
              type="file"
              accept="application/pdf"
              disabled={busy}
              onChange={(e) => pick(e.target.files?.[0] ?? null)}
              data-testid="agreement-file"
              className="cursor-pointer text-micro text-muted-foreground file:mr-3 file:cursor-pointer file:rounded-control file:border file:border-border file:bg-secondary file:px-3 file:py-1.5 file:font-heading file:text-data file:font-semibold file:text-secondary-foreground hover:file:bg-muted"
            />
            <p className="text-micro text-muted-foreground">
              {file ? `${file.name} · ${file_size(file.size)}` : 'PDF only, up to 10 MB.'}
            </p>
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="ag-effective">Effective from</Label>
            <input
              id="ag-effective"
              type="date"
              min={today}
              className="console-field w-48"
              value={effective}
              disabled={busy}
              onChange={(e) => set_effective(e.target.value)}
              data-testid="agreement-effective-from"
            />
            <p className="text-micro text-muted-foreground">
              {is_future
                ? `Vendors are notified now and the previous version stays in force until ${date_short(new Date(`${effective}T00:00:00`).toISOString())}.`
                : 'Takes effect immediately. Pick a future date to give vendors notice before it applies.'}
            </p>
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="ag-summary">What changed (optional)</Label>
            <textarea
              id="ag-summary"
              rows={4}
              maxLength={MAX_CHANGE_SUMMARY}
              value={summary}
              disabled={busy}
              onChange={(e) => set_summary(e.target.value)}
              placeholder="e.g. Cancellation window shortened to 12 hours; payout cycle now weekly."
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring"
              data-testid="agreement-change-summary"
            />
            <p className="flex justify-between text-micro text-muted-foreground">
              <span>Sent to vendors in the notification.</span>
              <span className="tabular">
                {summary.length}/{MAX_CHANGE_SUMMARY}
              </span>
            </p>
          </div>

          <p
            className="rounded-md border border-border bg-muted px-3 py-2 text-sm"
            data-testid="agreement-notify-line"
          >
            This will notify <strong>{plural(row.vendor_count, 'vendor')}</strong> offering {label}.
          </p>

          {error && (
            <p
              className="rounded-md border border-destructive/20 bg-destructive/10 px-3 py-2 text-sm text-destructive"
              data-testid="agreement-upload-error"
            >
              {error}
            </p>
          )}
        </div>

        <div className="mt-5 flex justify-end gap-3">
          <Button variant="outline" onClick={on_close} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="gradient"
            onClick={() => void submit()}
            disabled={busy || !file}
            data-testid="agreement-publish"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            Publish v{next_version}
          </Button>
        </div>
      </div>
    </div>
  );
}

/** Every version of one module's agreement, and who has accepted the selected one. */
function HistorySheet({
  row,
  on_close,
}: {
  row: AgreementModuleOverview | null;
  on_close: () => void;
}) {
  const module_type = row?.module_type ?? null;
  const versions_q = use_agreement_versions(module_type);
  const [selected_id, set_selected_id] = useState<string | null>(null);

  // Default to the newest version each time a module's history is opened.
  useEffect(() => {
    set_selected_id(null);
  }, [module_type]);
  const versions = versions_q.data;
  const selected =
    versions?.find((v) => v.id === selected_id) ?? (versions && versions.length ? versions[0] : null);

  return (
    <Sheet open={row !== null} onOpenChange={(o) => !o && on_close()}>
      <SheetContent
        className="flex w-full flex-col gap-0 overflow-y-auto sm:max-w-2xl"
        data-testid="agreement-history-sheet"
      >
        <SheetHeader className="text-left">
          <SheetTitle>{row ? `${module_label(row.module_type)} agreement history` : 'History'}</SheetTitle>
          <SheetDescription>
            {row ? `${plural(row.vendor_count, 'vendor')} offering this module` : ''}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 flex flex-col gap-6">
          <section>
            <h3 className="mb-2 font-heading text-sm font-bold">Versions</h3>
            {versions_q.error ? (
              <ErrorState
                title="Could not load the versions"
                description={error_message(versions_q.error)}
                action={
                  <Button size="sm" variant="outline" onClick={() => void versions_q.refetch()}>
                    Retry
                  </Button>
                }
              />
            ) : !versions ? (
              <PanelSkeleton rows={3} />
            ) : versions.length === 0 ? (
              <EmptyState icon={FileText} title="No versions published yet" />
            ) : (
              <ul className="divide-y divide-border rounded-md border border-border" data-testid="agreement-versions">
                {versions.map((v) => (
                  <VersionRow
                    key={v.id}
                    version={v}
                    accepted_count={v.accepted_count}
                    selected={selected?.id === v.id}
                    on_select={() => set_selected_id(v.id)}
                  />
                ))}
              </ul>
            )}
          </section>

          {selected && <VendorStatusList version={selected} />}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function VersionRow({
  version: v,
  accepted_count,
  selected,
  on_select,
}: {
  version: AgreementVersion;
  accepted_count: number;
  selected: boolean;
  on_select: () => void;
}) {
  return (
    <li
      className={cn('flex flex-wrap items-start gap-3 px-3 py-2.5', selected && 'bg-muted')}
      data-testid={`agreement-version-${v.version}`}
    >
      <button
        type="button"
        onClick={on_select}
        aria-pressed={selected}
        className="flex min-w-0 flex-1 flex-col gap-0.5 text-left"
      >
        <span className="flex flex-wrap items-center gap-1.5">
          <Badge tone={selected ? 'primary' : 'neutral'} size="sm">
            v{v.version}
          </Badge>
          <span className="text-sm">Published {date_short(v.published_at)}</span>
          {!v.in_effect && (
            <Badge tone="info" size="sm" dot>
              Upcoming
            </Badge>
          )}
        </span>
        <span className="text-micro text-muted-foreground">
          {v.in_effect ? 'Effective' : 'Takes effect'} {date_short(v.effective_from)} ·{' '}
          {v.original_filename} · {file_size(v.size_bytes)} · {accepted_count} accepted
        </span>
        {v.change_summary && (
          <span className="whitespace-pre-line text-micro text-foreground/80">{v.change_summary}</span>
        )}
      </button>
      <Button
        size="sm"
        variant="ghost"
        onClick={open_file_handler(v.file_path, (msg) => notify.error(msg))}
        aria-label={`Download v${v.version}`}
      >
        <Download className="h-4 w-4" />
      </Button>
    </li>
  );
}

function VendorStatusList({ version }: { version: AgreementVersion }) {
  const vendors_q = use_agreement_vendors(version.id);
  const [filter, set_filter] = useState<'PENDING' | 'ALL'>('PENDING');
  const all = vendors_q.data;
  const pending_count = all?.filter((v) => !v.accepted_at).length ?? 0;
  const shown = useMemo(
    () => (filter === 'PENDING' ? (all ?? []).filter((v) => !v.accepted_at) : (all ?? [])),
    [all, filter],
  );

  return (
    <section data-testid="agreement-vendor-status">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-heading text-sm font-bold">
          Vendors on v{version.version}
          {all && (
            <span className="ml-2 font-body text-micro font-normal text-muted-foreground">
              {all.length - pending_count} accepted · {pending_count} pending
            </span>
          )}
        </h3>
        <div className="flex rounded-md border border-border p-0.5" role="group" aria-label="Filter vendors">
          {(['PENDING', 'ALL'] as const).map((f) => (
            <button
              key={f}
              type="button"
              aria-pressed={filter === f}
              onClick={() => set_filter(f)}
              data-testid={`agreement-vendor-filter-${f}`}
              className={cn(
                'rounded px-2.5 py-1 text-xs font-medium transition-colors',
                filter === f
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {f === 'PENDING' ? 'Pending' : 'All'}
            </button>
          ))}
        </div>
      </div>

      {vendors_q.error ? (
        <ErrorState
          title="Could not load the vendors"
          description={error_message(vendors_q.error)}
          action={
            <Button size="sm" variant="outline" onClick={() => void vendors_q.refetch()}>
              Retry
            </Button>
          }
        />
      ) : !all ? (
        <PanelSkeleton rows={4} />
      ) : shown.length === 0 ? (
        <EmptyState
          icon={filter === 'PENDING' ? CheckCircle2 : FileText}
          title={
            filter === 'PENDING'
              ? all.length
                ? 'Every vendor has accepted this version'
                : 'No vendors offer this module'
              : 'No vendors offer this module'
          }
        />
      ) : (
        <ul className="divide-y divide-border rounded-md border border-border" data-testid="agreement-vendor-rows">
          {shown.map((v) => (
            <li key={v.vendor_profile_id} className="flex items-center justify-between gap-3 px-3 py-2">
              <div className="flex min-w-0 flex-col">
                <Link
                  to={`/vendors/${v.vendor_profile_id}`}
                  className="truncate text-sm font-semibold hover:underline"
                >
                  {v.business_name}
                </Link>
                <span className="tabular text-micro text-muted-foreground">{v.phone}</span>
              </div>
              {v.accepted_at ? (
                <div className="flex shrink-0 flex-col items-end gap-0.5">
                  <Badge tone="success" size="sm" dot>
                    Accepted
                  </Badge>
                  <span className="text-micro text-muted-foreground">
                    {date_short(v.accepted_at)}
                    {v.source ? ` · via ${ACCEPTANCE_SOURCE_LABEL[v.source]}` : ''}
                  </span>
                </div>
              ) : (
                <Badge tone="warning" size="sm" dot>
                  Pending
                </Badge>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
